import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import api, { apiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import { ImageCropDialog } from "@/components/ImageCropDialog";

/**
 * Inner form. State is initialised from `vendor` prop once on mount. The
 * parent re-mounts this component (via `key`) whenever the vendor changes,
 * so we never need useEffect to sync prop→state.
 */
const VendorEditForm = ({ vendor, endpoint, allowStatus, onSaved, onClose }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    farm_name: vendor.farm_name || "",
    farm_name_hr: vendor.farm_name_hr || "",
    region: vendor.region || "",
    region_hr: vendor.region_hr || "",
    story: vendor.story || "",
    story_hr: vendor.story_hr || "",
    contact_email: vendor.contact_email || "",
    website: vendor.website || "",
    lat: vendor.lat ?? "",
    lng: vendor.lng ?? "",
    cover_image: vendor.cover_image || "",
    avatar: vendor.avatar || "",
    status: vendor.status || "approved",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ cover_image: false, avatar: false });
  // Crop flow state: when the user picks a file we open the cropper instead
  // of uploading immediately. cropTarget tracks which field we're cropping for.
  const [cropTarget, setCropTarget] = useState(null); // {field: 'cover_image'|'avatar', src: blobUrl}
  const coverFileRef = useRef(null);
  const avatarFileRef = useRef(null);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // 1) User picks file → we read it into a blob URL and open the cropper.
  const onFilePicked = (field, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const src = URL.createObjectURL(file);
    setCropTarget({ field, src });
    // Reset the input so picking the same file again still fires onChange.
    const ref = field === "cover_image" ? coverFileRef : avatarFileRef;
    if (ref.current) ref.current.value = "";
  };

  // 2) User clicks Crop → upload the cropped blob to /upload and store the
  // returned path. Same backend endpoint products use.
  const onCropConfirm = async (blob) => {
    const { field, src } = cropTarget;
    setCropTarget(null);
    URL.revokeObjectURL(src);
    setUploading((s) => ({ ...s, [field]: true }));
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], `${field}.jpg`, { type: "image/jpeg" }));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField(field, data.path);
      toast.success(t("vendorEdit.uploaded"));
    } catch (err) {
      toast.error(apiError(err, t, "errors.generic"));
    } finally {
      setUploading((s) => ({ ...s, [field]: false }));
    }
  };

  const onCropCancel = () => {
    if (cropTarget) URL.revokeObjectURL(cropTarget.src);
    setCropTarget(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Only send fields whose value differs from the original vendor doc.
      // `exclude_unset` on the backend prevents clearing untouched fields.
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v === vendor[k] || (v === "" && (vendor[k] == null || vendor[k] === ""))) return;
        if (k === "lat" || k === "lng") {
          if (v === "") return;
          payload[k] = parseFloat(v);
        } else {
          payload[k] = v;
        }
      });
      if (!allowStatus) delete payload.status;
      const { data } = await api.put(endpoint, payload);
      toast.success(t("vendorEdit.saved", { count: data.updated || 0 }));
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e, t, "errors.generic"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-serif text-3xl">{t("vendorEdit.title")}</DialogTitle>
      </DialogHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
        <div>
          <Label htmlFor="farm_name">{t("vendorEdit.farmName")} (EN)</Label>
          <Input id="farm_name" data-testid="ve-farm-name" value={form.farm_name} onChange={(e) => setField("farm_name", e.target.value)} />
        </div>
        <div>
          <Label htmlFor="farm_name_hr">{t("vendorEdit.farmName")} (HR)</Label>
          <Input id="farm_name_hr" data-testid="ve-farm-name-hr" value={form.farm_name_hr} onChange={(e) => setField("farm_name_hr", e.target.value)} />
        </div>
        <div>
          <Label htmlFor="region">{t("vendorEdit.region")} (EN)</Label>
          <Input id="region" data-testid="ve-region" value={form.region} onChange={(e) => setField("region", e.target.value)} />
        </div>
        <div>
          <Label htmlFor="region_hr">{t("vendorEdit.region")} (HR)</Label>
          <Input id="region_hr" data-testid="ve-region-hr" value={form.region_hr} onChange={(e) => setField("region_hr", e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="story">{t("vendorEdit.story")} (EN)</Label>
          <Textarea id="story" data-testid="ve-story" rows={4} value={form.story} onChange={(e) => setField("story", e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="story_hr">{t("vendorEdit.story")} (HR)</Label>
          <Textarea id="story_hr" data-testid="ve-story-hr" rows={4} value={form.story_hr} onChange={(e) => setField("story_hr", e.target.value)} />
        </div>
        <div>
          <Label htmlFor="contact_email">{t("vendorEdit.contactEmail")}</Label>
          <Input id="contact_email" type="email" data-testid="ve-contact-email" value={form.contact_email} onChange={(e) => setField("contact_email", e.target.value)} placeholder="info@yourfarm.hr" />
        </div>
        <div>
          <Label htmlFor="website">{t("vendorEdit.website")}</Label>
          <Input id="website" data-testid="ve-website" value={form.website} onChange={(e) => setField("website", e.target.value)} placeholder="https://yourfarm.hr" />
        </div>
        <div>
          <Label htmlFor="lat">{t("vendorEdit.lat")}</Label>
          <Input id="lat" type="number" step="0.0001" data-testid="ve-lat" value={form.lat} onChange={(e) => setField("lat", e.target.value)} />
        </div>
        <div>
          <Label htmlFor="lng">{t("vendorEdit.lng")}</Label>
          <Input id="lng" type="number" step="0.0001" data-testid="ve-lng" value={form.lng} onChange={(e) => setField("lng", e.target.value)} />
        </div>

        {/* Cover photo — upload to the same /upload endpoint products use */}
        <div className="md:col-span-2">
          <Label>{t("vendorEdit.coverImage")}</Label>
          {form.cover_image ? (
            <div className="mt-1 relative w-full h-48 rounded-sm overflow-hidden hairline group">
              <img src={fileUrl(form.cover_image)} alt="" className="w-full h-full object-cover" data-testid="ve-cover-preview" />
              <button
                type="button"
                onClick={() => setField("cover_image", "")}
                className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-bone p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid="ve-cover-remove"
                aria-label={t("common.delete")}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => coverFileRef.current?.click()}
              disabled={uploading.cover_image}
              className="mt-1 w-full h-48 hairline border-dashed rounded-sm flex flex-col items-center justify-center gap-2 text-ink-muted hover:border-olive hover:text-olive transition-colors"
              data-testid="ve-cover-upload"
            >
              <Upload className="w-5 h-5" />
              <span className="text-xs uppercase tracking-widest">{uploading.cover_image ? t("vendorEdit.uploading") : t("vendorEdit.uploadCover")}</span>
            </button>
          )}
          <input ref={coverFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFilePicked("cover_image", e)} data-testid="ve-cover-file-input" />
        </div>

        {/* Avatar */}
        <div className="md:col-span-2">
          <Label>{t("vendorEdit.avatar")}</Label>
          {form.avatar ? (
            <div className="mt-1 relative w-24 h-24 rounded-full overflow-hidden hairline group">
              <img src={fileUrl(form.avatar)} alt="" className="w-full h-full object-cover" data-testid="ve-avatar-preview" />
              <button
                type="button"
                onClick={() => setField("avatar", "")}
                className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-bone p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid="ve-avatar-remove"
                aria-label={t("common.delete")}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => avatarFileRef.current?.click()}
              disabled={uploading.avatar}
              className="mt-1 w-24 h-24 hairline border-dashed rounded-full flex items-center justify-center text-ink-muted hover:border-olive hover:text-olive transition-colors"
              data-testid="ve-avatar-upload"
              aria-label={t("vendorEdit.uploadAvatar")}
            >
              <Upload className="w-4 h-4" />
            </button>
          )}
          <input ref={avatarFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFilePicked("avatar", e)} data-testid="ve-avatar-file-input" />
        </div>

        {allowStatus && (
          <div className="md:col-span-2 mt-2 pt-4 border-t border-edge">
            <Label htmlFor="status">{t("vendorEdit.status")}</Label>
            <select
              id="status"
              data-testid="ve-status"
              value={form.status}
              onChange={(e) => setField("status", e.target.value)}
              className="mt-1 block w-full rounded-sm border border-edge bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-olive"
            >
              <option value="pending">{t("vendorEdit.statusPending")}</option>
              <option value="approved">{t("vendorEdit.statusApproved")}</option>
              <option value="rejected">{t("vendorEdit.statusRejected")}</option>
            </select>
            <p className="text-xs text-ink-muted mt-2">{t("vendorEdit.statusHint")}</p>
          </div>
        )}
      </div>

      <DialogFooter className="mt-6 gap-2">
        <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="ve-cancel">
          {t("common.cancel")}
        </Button>
        <Button onClick={save} disabled={saving} className="bg-olive hover:bg-olive-dark text-bone rounded-sm" data-testid="ve-save">
          {saving ? t("common.saving") : t("common.save")}
        </Button>
      </DialogFooter>

      {cropTarget && (
        <ImageCropDialog
          imageSrc={cropTarget.src}
          aspect={cropTarget.field === "cover_image" ? 16 / 9 : 1}
          shape={cropTarget.field === "cover_image" ? "rect" : "round"}
          onConfirm={onCropConfirm}
          onCancel={onCropCancel}
        />
      )}
    </>
  );
};

/**
 * Vendor profile editor dialog. Used in two contexts:
 *  - Admin Panel  → endpoint=`/admin/vendors/{id}`, allowStatus=true
 *  - Vendor self  → endpoint=`/vendor/profile`,    allowStatus=false
 */
export const VendorEditDialog = ({ open, onOpenChange, vendor, endpoint, allowStatus = false, onSaved }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-bone" data-testid="vendor-edit-dialog">
        {vendor && (
          <VendorEditForm
            key={vendor.vendor_id || "new"}
            vendor={vendor}
            endpoint={endpoint}
            allowStatus={allowStatus}
            onSaved={onSaved}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VendorEditDialog;
